import {useCallback, useEffect, useMemo, useRef} from 'react'
import {StyleSheet, View} from 'react-native'
import {createVideoPlayer, VideoView} from 'expo-video'

import {COVER_VIDEO_MAX_MS, coverWindow} from '#/lib/coverTrim'
import {IS_WEB} from '#/env'

type Props = {
  uri: string
  /**
   * Where the cover opens, in ms.
   *
   * A film longer than its window plays only the chosen part (see
   * `#/lib/coverTrim`), so this is not decoration: without it the learner meets
   * the film's opening rather than the minute the creator chose.
   */
  startMs?: number
  /**
   * How long that window runs, because the two surfaces want different lengths.
   *
   * A FlashBook cover is a sixty-second teaser; a Capsule cover is the intro to a
   * quiz and may run three minutes. Undefined means the book's, which is what every
   * caller that has no opinion about it gets.
   */
  windowMs?: number
  /** Stop at the end of the window and tell the caller, rather than playing on. */
  onEnded?: () => void
  /** Start playing as soon as the film is ready. */
  autoPlay?: boolean
  /**
   * Whether the film plays at all, when the *owner* wants to drive it.
   *
   * `autoPlay` starts a film and then leaves it alone; this is for a caller with
   * a transport — the trimmer's play/pause button, and the studio, which has to
   * hold its own preview still while the trimmer is showing the same film.
   * Undefined means "nobody is driving me", and playback follows `autoPlay` and
   * the window's own end.
   */
  playing?: boolean
  /**
   * Sound off. **On by default.**
   *
   * This used to be the other way round, and that was the defect: `autoPlay`
   * meant *unmuted* as well as *playing*, so every preview in the studio — the
   * composer's own stage, and the trimmer looping the chosen minute — started
   * talking over the creator the moment a film was picked, and was still going if
   * the screen it belonged to went away mid-loop. A preview now arrives silent and
   * has to be asked to make a sound, which is also the only way an autoplay is
   * allowed to start in a browser at all.
   */
  muted?: boolean
  /**
   * Play the window over and over.
   *
   * On for the *trimmer*, where the creator is looking at the minute they are
   * choosing and needs to see it repeat; off everywhere a learner meets the book,
   * where a cover teaser plays once and settles.
   */
  loopWindow?: boolean
  /**
   * The platform's own transport bar.
   *
   * On everywhere the learner meets the cover — a teaser and its sound want a
   * play button — and off in the trimmer, where the scrubber *is* the control and
   * a second bar under it would offer a way to seek outside the window being
   * chosen.
   */
  showControls?: boolean
  /**
   * Whether the film is making a sound right now.
   *
   * *Reported* rather than owned, because on a cover it is the learner who starts
   * the film — the transport bar is the platform's own — so a caller cannot know
   * from a prop. It exists so a play surface can treat "the film is on" exactly
   * like "a take is playing": both mean the background music must step back (see
   * `#/lib/musicDuck`), and a film talking over the bed is a miss nobody would
   * otherwise be able to see.
   */
  onPlayingChange?: (playing: boolean) => void
  style?: object
}

/**
 * A film, played inside its window.
 *
 * Web uses a plain `<video>` element; native uses expo-video. The window is read
 * from the element's *own* duration wherever it is known, so a film already on a
 * server — whose length never travelled with the record — is still trimmed
 * correctly rather than played whole.
 *
 * Shared by both studios rather than owned by one: a cover window is a cover
 * window, and the Capsule studio needs exactly the behaviour the FlashBook one
 * spent its defects learning — silent on arrival, held still under a finger, never
 * playing the part that was cut, and stopping when the screen it belongs to goes
 * away.
 */
export function StudioWindowVideo({
  uri,
  startMs = 0,
  windowMs = COVER_VIDEO_MAX_MS,
  onEnded,
  autoPlay = false,
  playing,
  muted = true,
  loopWindow = false,
  showControls = true,
  onPlayingChange,
  style,
}: Props) {
  if (IS_WEB) {
    return (
      <WebCoverVideo
        uri={uri}
        startMs={startMs}
        windowMs={windowMs}
        onEnded={onEnded}
        autoPlay={autoPlay}
        playing={playing}
        muted={muted}
        loopWindow={loopWindow}
        showControls={showControls}
        onPlayingChange={onPlayingChange}
        style={style}
      />
    )
  }
  return (
    <NativeCoverVideo
      uri={uri}
      startMs={startMs}
      windowMs={windowMs}
      onEnded={onEnded}
      autoPlay={autoPlay}
      playing={playing}
      muted={muted}
      loopWindow={loopWindow}
      showControls={showControls}
      onPlayingChange={onPlayingChange}
      style={style}
    />
  )
}

function WebCoverVideo({
  uri,
  startMs,
  windowMs,
  onEnded,
  autoPlay,
  playing,
  muted,
  loopWindow,
  showControls,
  onPlayingChange,
  style,
}: {
  uri: string
  startMs: number
  windowMs: number
  onEnded?: () => void
  autoPlay?: boolean
  playing?: boolean
  muted: boolean
  loopWindow?: boolean
  showControls?: boolean
  onPlayingChange?: (playing: boolean) => void
  style?: object
}) {
  const ref = useRef<HTMLVideoElement | null>(null)
  const onEndedRef = useRef(onEnded)
  onEndedRef.current = onEnded
  const onPlayingChangeRef = useRef(onPlayingChange)
  onPlayingChangeRef.current = onPlayingChange
  const startRef = useRef(startMs)
  startRef.current = startMs
  const loopRef = useRef(loopWindow)
  loopRef.current = loopWindow
  /*
   * Read through a ref inside the listeners, so changing the sound or the
   * transport does not tear down and re-add every event handler (which would also
   * re-seek the film, making a mute tap jump the picture back to the window's
   * start).
   */
  const mutedRef = useRef(muted)
  mutedRef.current = muted
  const playingRef = useRef(playing)

  /** The window, read against whatever length the element knows right now. */
  const windowOf = (video: HTMLVideoElement) =>
    coverWindow(
      Number.isFinite(video.duration) ? video.duration * 1000 : undefined,
      startRef.current,
      windowMs,
    )

  /**
   * Put the playhead at the chosen opening.
   *
   * Called when the window changes (so dragging the scrubber shows the frame the
   * creator just chose) and when the metadata arrives — assigning `currentTime`
   * before the element knows how long the film is does nothing at all, which is
   * exactly the case for a film served from the network.
   */
  const seekToOpening = useCallback(() => {
    const video = ref.current
    if (!video) return
    const {startMs: at} = windowOf(video)
    const target = at / 1000
    if (Math.abs(video.currentTime - target) > 0.12) {
      video.currentTime = target
    }
  }, [])

  /** Whether anything at all should be playing right now. */
  const wantsPlay = playing ?? autoPlay
  playingRef.current = wantsPlay

  useEffect(() => {
    const video = ref.current
    if (!video) return
    video.muted = muted
  }, [muted, uri])

  useEffect(() => {
    const video = ref.current
    if (!video) return
    const onLoaded = () => {
      seekToOpening()
      if (playingRef.current) void video.play().catch(() => {})
    }
    /*
     * Whoever started it, the film is only ever the creator's minute.
     *
     * The end check used to return early when the *owner* was not driving
     * playback, which is right for the loop and wrong for the window's end: a
     * learner pressing the control bar's own play button on the cover ran
     * straight past the minute the creator chose and on to the end of the file,
     * which is the one promise the trimmer exists to make, broken for the only
     * person it was made to.
     */
    const onTime = () => {
      const {endMs} = windowOf(video)
      if (video.currentTime * 1000 < endMs - 40) return
      if (loopRef.current && playingRef.current) {
        video.currentTime = windowOf(video).startMs / 1000
        void video.play().catch(() => {})
        return
      }
      video.pause()
      onEndedRef.current?.()
    }
    /*
     * And it never plays the part that was cut.
     *
     * A learner's play can start anywhere — at 0:00 on a cover whose minute
     * begins at 1:00, or again after the window has just ended — so playback is
     * pulled to the opening rather than trusted to have begun there. Without
     * this, pressing play on the cover showed a different minute of the film
     * from the one the creator trimmed, which is worse than showing none.
     */
    const onPlay = () => {
      const {startMs: at, endMs} = windowOf(video)
      const nowMs = video.currentTime * 1000
      if (nowMs < at - 40 || nowMs >= endMs - 40) video.currentTime = at / 1000
      onPlayingChangeRef.current?.(true)
    }
    const onPause = () => onPlayingChangeRef.current?.(false)
    const onEnd = () => {
      onPlayingChangeRef.current?.(false)
      onEndedRef.current?.()
    }
    video.addEventListener('loadedmetadata', onLoaded)
    video.addEventListener('timeupdate', onTime)
    video.addEventListener('play', onPlay)
    video.addEventListener('pause', onPause)
    video.addEventListener('ended', onEnd)
    // A film already in cache fires nothing when the element is created.
    if (video.readyState >= 1) onLoaded()
    return () => {
      video.removeEventListener('loadedmetadata', onLoaded)
      video.removeEventListener('timeupdate', onTime)
      video.removeEventListener('play', onPlay)
      video.removeEventListener('pause', onPause)
      video.removeEventListener('ended', onEnd)
      /*
       * Stop the film on the way out, and let go of the resource.
       *
       * Removing the element usually stops playback by itself, but "usually" is
       * what left a creator hearing their own film after they had closed the
       * studio: the teardown of a screen is not a point at which anything should
       * be left to the browser. Pausing is what silences it, and clearing the
       * source is what stops the download too — a film can be tens of megabytes,
       * and this one is no longer wanted by anybody.
       */
      try {
        video.pause()
        video.removeAttribute('src')
        video.load()
      } catch {}
    }
  }, [uri, seekToOpening])

  /*
   * The owner's transport. Driven from an effect *outside* the listener effect
   * above, whose dependencies deliberately exclude this: pausing must not throw
   * away the listeners, and the film must not be re-seeked to make it stop.
   */
  useEffect(() => {
    const video = ref.current
    if (!video) return
    if (!wantsPlay) {
      video.pause()
      return
    }
    // Past the window's end already (the owner paused on the last frame)? Start
    // again from the opening rather than from nowhere.
    const {startMs: at, endMs} = windowOf(video)
    if (video.currentTime * 1000 >= endMs - 40) video.currentTime = at / 1000
    void video.play().catch(() => {})
  }, [wantsPlay, uri])

  // Dragging the scrubber moves the opening, so follow it.
  useEffect(() => {
    seekToOpening()
  }, [startMs, seekToOpening])

  return (
    <View style={[styles.wrap, style]}>
      <video
        // @ts-ignore RN-web custom element
        ref={ref}
        src={uri}
        playsInline
        preload="metadata"
        style={{width: '100%', height: '100%', objectFit: 'cover'}}
        controls={showControls}
        muted={muted}
      />
    </View>
  )
}

function NativeCoverVideo({
  uri,
  startMs,
  windowMs,
  onEnded,
  autoPlay,
  playing,
  muted,
  loopWindow,
  showControls,
  onPlayingChange,
  style,
}: {
  uri: string
  startMs: number
  windowMs: number
  onEnded?: () => void
  autoPlay?: boolean
  playing?: boolean
  muted: boolean
  loopWindow?: boolean
  showControls?: boolean
  onPlayingChange?: (playing: boolean) => void
  style?: object
}) {
  /*
   * One player per film, not one per render.
   *
   * This used to be a bare `createVideoPlayer(uri)` in the component body, so
   * every state change — every drag of the scrubber, every tap of the sound
   * button — built a new native player and handed the old one to the cleanup to
   * release. A release happens asynchronously on a device, so a drag could leave
   * several players of the same film alive at once: they played on top of each
   * other, and the *last* one to be released was not necessarily the last one
   * making noise. Memoised on the uri, there is exactly one, and the cleanup
   * below closes it.
   */
  const player = useMemo(() => createVideoPlayer(uri), [uri])
  player.loop = false
  player.muted = muted
  // The window's own edge has to be noticed within a frame or two: this is the
  // difference between a 60-second cover and a 60-second cover plus a jump cut.
  player.timeUpdateEventInterval = 0.2
  const startRef = useRef(startMs)
  startRef.current = startMs
  const wantsPlay = playing ?? autoPlay
  const wantsPlayRef = useRef(wantsPlay)
  wantsPlayRef.current = wantsPlay
  // Read through a ref inside the listeners, so a caller passing an inline
  // handler does not tear down and re-add every subscription each render.
  const onPlayingChangeRef = useRef(onPlayingChange)
  onPlayingChangeRef.current = onPlayingChange

  useEffect(() => {
    const windowOf = () =>
      coverWindow(
        Number.isFinite(player.duration) ? player.duration * 1000 : undefined,
        startRef.current,
        windowMs,
      )
    const subs = [
      player.addListener('sourceLoad', ({duration}) => {
        const {startMs: at} = coverWindow(
          duration * 1000,
          startRef.current,
          windowMs,
        )
        player.currentTime = at / 1000
        if (wantsPlayRef.current) void player.play()
      }),
      player.addListener('timeUpdate', ({currentTime}) => {
        const {startMs: at, endMs} = windowOf()
        // Seeking is asynchronous on a device: while the seek is in flight the
        // player can still report a position before the window, and treating that
        // as "the film is over" would end the cover on its first frame.
        if (currentTime < at / 1000 - 0.4) {
          player.currentTime = at / 1000
          return
        }
        if (currentTime * 1000 < endMs - 60) return
        if (loopWindow) {
          player.currentTime = at / 1000
          void player.play()
          return
        }
        player.pause()
        onEnded?.()
      }),
      player.addListener('playToEnd', () => {
        onPlayingChangeRef.current?.(false)
        onEnded?.()
      }),
      // The platform's own controls can start this film, so "is it playing" is
      // read from the player rather than inferred from `playing`.
      player.addListener('playingChange', ({isPlaying}) =>
        onPlayingChangeRef.current?.(isPlaying),
      ),
    ]
    return () => {
      for (const sub of subs) sub.remove()
    }
  }, [player, onEnded, loopWindow, windowMs])

  useEffect(() => {
    player.muted = muted
  }, [player, muted])

  /*
   * The owner's transport: play, or hold still. `isPlaying` is deliberately not a
   * dependency — a film reaching the end of its window flips it, and re-running
   * this on that would start it again, turning a window into an infinite loop.
   */
  useEffect(() => {
    if (wantsPlay) {
      if (!player.playing) void player.play()
    } else {
      if (player.playing) player.pause()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [player, wantsPlay, uri])

  // The creator dragged the scrubber: show them the new opening.
  useEffect(() => {
    if (!Number.isFinite(player.duration) || player.duration <= 0) return
    const {startMs: at} = coverWindow(player.duration * 1000, startMs, windowMs)
    if (Math.abs(player.currentTime - at / 1000) > 0.2) {
      player.currentTime = at / 1000
    }
  }, [player, startMs, windowMs])

  /*
   * Let the player go on the way out — and stop it first.
   *
   * `release()` is meant to be enough, but it is asynchronous on a device, and in
   * the gap the film is still running. Pausing first is what actually silences the
   * screen the creator just left.
   */
  useEffect(() => {
    return () => {
      try {
        player.pause()
        player.muted = true
      } catch {}
      player.release()
      // Leaving is not pressing stop, and a bed left ducked is a bed that stays
      // quiet for the rest of the book.
      onPlayingChangeRef.current?.(false)
    }
  }, [player])

  return (
    <View style={[styles.wrap, style]}>
      <VideoView
        player={player}
        style={styles.video}
        contentFit="cover"
        nativeControls={showControls}
      />
    </View>
  )
}

const styles = StyleSheet.create({
  wrap: {overflow: 'hidden'},
  video: {width: '100%', height: '100%'},
})
